import React, { useState, useEffect, useRef } from 'react';
import styles from './SupportForm.module.scss';
import {
  SUPPORT_REQUEST_REASONS,
  SUPPORT_REQUEST_HASH,
} from '../../../../../commons/Constants';
import { useAppDispatch, useAppSelector } from '../../../../../src/app/hooks';
import {
  supportTicketInfo,
  resetSupportTicket,
} from '../../../../../src/features/user/userState';
import { useRouter } from 'next/router';
import CancelOptions from '../../../../body/support/CancelOptions/CancelOptions';
import { Typography } from '@mui/material';
import {
  ContainedButton,
  CountryDropdown,
  TextWithArrowAnimation,
} from '../../../../../commons/hoc';
import RestartForm from '../../forms/RestartForm';
import ChangeDateForm from '../../forms/ChangeDateForm';
import ChangeAddressForm from '../../forms/ChangeAddressForm';
import QuestionForm from '../../forms/QuestionForm';
import ProductForm from '../../forms/ProductForm';
import FrequencyForm from '../../forms/FrequencyForm';
import CancelForm from '../../forms/CancelForm';

const SupportForm = ({ refAccordianCallback }) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const ddSupportRef = useRef(null);
  const [currentOption, setCurrentOption] = useState(
    SUPPORT_REQUEST_HASH[router?.query?.option] || 'choose'
  );
  const [controlCancel, setControlCancel] = useState(
    SUPPORT_REQUEST_HASH[router?.query?.option] == 'cancel'
  );
  const [submitanother, setSubmitAnother] = useState(false);
  const supportTicketDetails = useAppSelector(supportTicketInfo);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (supportTicketDetails?.success) {
      setCurrentOption('choose');
      setSubmitAnother(false);
      handleScrollafterSuccess();
    }
  }, [supportTicketDetails]);

  const onDropdownChange = (e) => {
    let value = e.target.value;
    let hash = '';
    Object.keys(SUPPORT_REQUEST_HASH).some((key) => {
      if (SUPPORT_REQUEST_HASH[key] === value) {
        hash = key;
        return true;
      }
      return false;
    });
    if (supportTicketDetails) dispatch(resetSupportTicket());
    if (controlCancel) {
      setControlCancel(false);
    }
    if (value == 'cancel') {
      setControlCancel(true);
    }
    router.replace(
      router.pathname,
      {
        query: {
          option: hash,
        },
      },
      { scroll: false }
    );
    setCurrentOption(value);
  };

  const handleScrollafterSuccess = () => {
    ddSupportRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const DropdownOptions = () => {
    useEffect(() => {
      if (currentOption == 'choose') {
        window.scrollTo(0, ddSupportRef.current.offsetTop - 300);
      } else {
        ddSupportRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, [currentOption]);

    switch (currentOption) {
      case 'restart':
        return (
          <RestartForm
            currentOption={currentOption}
            setFormSubmitted={setFormSubmitted}
          />
        );
      case 'date':
        return (
          <ChangeDateForm
            currentOption={currentOption}
            setFormSubmitted={setFormSubmitted}
          />
        );
      case 'address':
        return (
          <ChangeAddressForm
            currentOption={currentOption}
            setFormSubmitted={setFormSubmitted}
          />
        );
      case 'question':
        return (
          <QuestionForm
            type="question"
            currentOption={currentOption}
            setFormSubmitted={setFormSubmitted}
          />
        );
      case 'frequency':
        return (
          <FrequencyForm
            currentOption={currentOption}
            setFormSubmitted={setFormSubmitted}
          />
        );
      case 'product':
        return (
          <ProductForm
            currentOption={currentOption}
            setFormSubmitted={setFormSubmitted}
          />
        );
      case 'other':
        return (
          <QuestionForm
            type="request"
            currentOption={currentOption}
            setFormSubmitted={setFormSubmitted}
          />
        );
      case 'cancel':
        return controlCancel ? (
          <CancelOptions
            onClickHandler={setCurrentOption}
            toggleCancel={setControlCancel}
          />
        ) : (
          <CancelForm
            currentOption={currentOption}
            setFormSubmitted={setFormSubmitted}
          />
        );
      default:
        return <div></div>;
    }
  };

  const submitAnotherAction = () => {
    setSubmitAnother(true);
  };

  return (
    <>
      <div className={styles.formExtender}>
        <div className={styles.container}>
          <div className={styles.formContentContainer}>
            <div id="ddSupportId" ref={ddSupportRef}></div>
            <div
              className={styles.formContentHeader}
              id="request"
              data-section
              ref={refAccordianCallback}
            >
              <div className={styles.formHeaderContainer}>
                <Typography
                  variant="h2"
                  color="primary.main"
                  className={styles.formHeader}
                >
                  Open a Support Request
                </Typography>
                <Typography
                  variant="p1"
                  color="primary.black"
                  className={styles.formDescription}
                >
                  You will receive a support ticket confirmation via email after
                  submitting your request. Most support requests are resolved in
                  2 business days or less.
                </Typography>
                {supportTicketDetails &&
                supportTicketDetails?.success &&
                !submitanother &&
                formSubmitted ? (
                  <div className={styles.formSubmitMsgContainer}>
                    <Typography
                      variant="button1"
                      color="primary.main"
                      className={styles.formSubmitMsgContent}
                      component="p"
                    >
                      Thank you for submitting your ticket.
                    </Typography>
                    <ContainedButton
                      btnType={'dark'}
                      className={styles.submitAnotherBtn}
                      onClick={submitAnotherAction}
                    >
                      <div className={styles.submitAnotherBtnContainer}>
                        <div>
                          <Typography
                            variant="button2"
                            color="primary.white"
                            component="span"
                          >
                            Submit Another
                          </Typography>
                        </div>
                        <div>
                          <TextWithArrowAnimation type="light" />
                        </div>
                      </div>
                    </ContainedButton>
                  </div>
                ) : (
                  <div className={styles.formContainer}>
                    <form>
                      <div className={styles.formFieldContainer}>
                        <CountryDropdown
                          id="supportOpts"
                          name="supportOptions"
                          isDarkBorder={true}
                          options={Object.values(SUPPORT_REQUEST_REASONS)}
                          label={'Select Request Type'}
                          tkey={'name'}
                          value={currentOption}
                          onChange={onDropdownChange}
                        />
                      </div>
                    </form>
                    <div>
                      <DropdownOptions />
                    </div>
                    {supportTicketDetails && (
                      <div className={styles.supportAlertWrapper}>
                        {!supportTicketDetails?.success && formSubmitted && (
                          <div
                            className={`${styles.supportAlertMessage} ${styles.supportAlertError}`}
                          >
                            <Typography color="primary.black" variant="p1">
                              {supportTicketDetails?.message}
                            </Typography>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportForm;
